import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useGlobalSearch, getGlobalSearchQueryKey } from "@workspace/api-client-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search } from "lucide-react";

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [, setLocation] = useLocation();
  const debouncedQuery = useDebounced(query.trim(), 200);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const enabled = debouncedQuery.length > 0;
  const { data, isFetching } = useGlobalSearch(
    { q: debouncedQuery },
    { query: { enabled, staleTime: 30_000, queryKey: getGlobalSearchQueryKey({ q: debouncedQuery }) } },
  );

  const navigate = (href: string) => {
    setOpen(false);
    const hashIdx = href.indexOf("#");
    if (hashIdx >= 0) {
      const path = href.slice(0, hashIdx);
      const hash = href.slice(hashIdx);
      setLocation(path);
      setTimeout(() => {
        if (window.location.hash !== hash) {
          window.location.hash = hash;
        } else {
          window.dispatchEvent(new HashChangeEvent("hashchange"));
        }
      }, 0);
    } else {
      setLocation(href);
    }
  };

  const groups: Array<{ label: string; items: { id: number; primary: string; secondary?: string | null; href: string }[] }> = [];
  if (data) {
    if (data.coaches.length) groups.push({ label: "Coaches", items: data.coaches });
    if (data.contracts.length) groups.push({ label: "Contracts", items: data.contracts });
    if (data.documents.length) groups.push({ label: "Documents", items: data.documents });
    if (data.notes.length) groups.push({ label: "Notes", items: data.notes });
    if (data.mitigations.length) groups.push({ label: "Insurance & Mitigations", items: data.mitigations });
  }

  const totalResults =
    (data?.coaches.length ?? 0) +
    (data?.contracts.length ?? 0) +
    (data?.documents.length ?? 0) +
    (data?.notes.length ?? 0) +
    (data?.mitigations.length ?? 0);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={() => setOpen(true)}
        className="h-10 w-10 sm:hidden"
        aria-label="Open search"
        data-testid="button-open-command-palette-mobile"
      >
        <Search className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen(true)}
        className="hidden sm:flex h-9 w-full max-w-md justify-start gap-2 px-3 text-sm font-normal text-muted-foreground"
        data-testid="button-open-command-palette"
      >
        <Search className="h-4 w-4" />
        <span>Search coaches, contracts, mitigations…</span>
        <kbd className="ml-auto hidden items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] text-muted-foreground sm:inline-flex">
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <DialogTitle className="sr-only">Search</DialogTitle>
        <DialogDescription className="sr-only">
          Search across coaches, contracts, and insurance/mitigation records.
        </DialogDescription>
        <CommandInput
          placeholder="Search coaches, contracts, mitigations…"
          value={query}
          onValueChange={setQuery}
          data-testid="input-command-palette"
        />
        <CommandList>
          {!enabled ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Start typing to search across coaches, contracts, and mitigations.
            </div>
          ) : isFetching && !data ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Searching…</div>
          ) : totalResults === 0 ? (
            <CommandEmpty>No results found for &ldquo;{debouncedQuery}&rdquo;.</CommandEmpty>
          ) : (
            groups.map((group) => (
              <CommandGroup key={group.label} heading={group.label}>
                {group.items.map((item) => (
                  <CommandItem
                    key={`${group.label}-${item.id}`}
                    value={`${group.label}-${item.id}-${item.primary}-${item.secondary ?? ""}`}
                    onSelect={() => navigate(item.href)}
                    data-testid={`command-result-${group.label.toLowerCase().split(" ")[0]}-${item.id}`}
                  >
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <span className="truncate font-medium">{item.primary}</span>
                      {item.secondary && (
                        <span className="truncate text-xs text-muted-foreground">{item.secondary}</span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
